import React, { useState, useMemo, useCallback, useEffect } from "react";
import Modal from "react-modal";
import axios from "axios";

Modal.setAppElement("#root");

const StatusChangeModal = ({
  isOpen,
  onClose,
  userId,
  currentStatus,
  newStatus,
  newStatusLabel,
  onSuccess,
}) => {
  const [reason, setReason] = useState("");
  const [statusReasons, setStatusReasons] = useState([]);
  const [effectiveDate, setEffectiveDate] = useState("");
  const [note, setNote] = useState("");
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  const segments = useMemo(
    () => ["Candidate", "Future Candidate", "Prospect", "Inactive"],
    []
  );

  useEffect(() => {
    const fetchStatusReasons = async () => {
      try {
        const res = await axios.get(
          `${API_BASE}/api/dropdowns/status-reasons`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        setStatusReasons(res.data || []);
      } catch (err) {
        console.error("❌ Failed to load status reasons:", err);
      }
    };

    if (isOpen) {
      fetchStatusReasons();
    }
  }, [isOpen, API_BASE]);

  const onFilePicked = (f) => {
    if (!f) return;
    setFile(f);
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    const f = e.dataTransfer?.files?.[0];
    onFilePicked(f);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  }, []);

  const handleSubmit = async () => {
    if (!userId || !newStatus || !reason) {
      alert("Reason and target status are required.");
      return;
    }

    const fd = new FormData();
    if (file) fd.append("file", file);
    fd.append("userId", userId);
    fd.append("oldStatus", currentStatus);
    fd.append("newStatus", newStatus);
    fd.append("reason", reason); // sr_id
    fd.append("effectiveDate", effectiveDate);
    fd.append("note", note || "");

    try {
      await axios.post(
        `${API_BASE}/api/user/status-change?userId=${userId}`,
        fd,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      onSuccess?.();
      onClose?.();
      // ✅ Automatically refresh page after successful change
      setTimeout(() => {
        window.location.reload();
      }, 500);
    } catch (err) {
      console.error("Error saving status change:", err);
      alert(err.response?.data?.message || "Save failed.");
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      className="status-modal"
      overlayClassName="status-overlay"
    >
      <div className="scm-seg">
        {segments.map((label) => (
          <div
            key={label}
            className={`scm-seg-item ${
              label === (newStatusLabel || "") ? "active" : ""
            }`}
            title={label}
          >
            {label}
          </div>
        ))}
      </div>

      <div className="scm-form">
        <div className="scm-field">
          <label className="scm-label">
            Reason for Change <span className="req">*</span>
          </label>
          <div className="scm-select-wrap">
            <select
              className="scm-input"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              required
            >
              <option value="">Select Reason</option>
              {statusReasons.map((r) => (
                <option key={r.sr_id} value={r.sr_id}>
                  {r.sr_name}
                </option>
              ))}
            </select>
            <i className="fa fa-chevron-down scm-select-caret" />
          </div>
        </div>

        <div className="scm-field">
          <label className="scm-label">
            Effective As Of <span className="req">*</span>
          </label>
          <input
            type="date"
            className="scm-input"
            value={effectiveDate}
            onChange={(e) => setEffectiveDate(e.target.value)}
            required
          />
        </div>

        <div className="scm-field">
          <label className="scm-label">Status Change Note</label>
          <textarea
            className="scm-textarea"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder=""
          />
        </div>

        <div className="scm-field">
          <div className="scm-docs-title">Documents</div>

          <div
            className={`scm-dropzone ${dragOver ? "drag" : ""}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <div className="scm-drop-contents">
              <i className="fa fa-upload scm-upload-icon" />
              <div className="scm-drop-text">
                Drag & Drop your files or Browse
              </div>
              {file && <div className="scm-file-name">{file.name}</div>}
            </div>
            <input
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.msg"
              className="scm-file-input"
              onChange={(e) => onFilePicked(e.target.files?.[0])}
            />
          </div>
        </div>

        <div className="scm-actions">
          <button
            type="button"
            className="scm-btn scm-btn-cancel"
            onClick={onClose}
          >
            <i className="fa fa-ban me-1" />
            Cancel
          </button>
          <button
            type="button"
            className="scm-btn scm-btn-save"
            onClick={handleSubmit}
            disabled={!reason}
            title={!reason ? "Reason is required" : "Save Change"}
          >
            <i className="fa fa-save me-1" />
            Save Change
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default StatusChangeModal;
